import { FC, useState } from 'react';
import { ContentCommentary, ShareContentDestination, ShareType, SocialAccount } from 'api';
import React from 'react';
import {
  Network,
  useCreatePublicShareInfoMutation,
  usePublicShareContentMutation,
} from 'api/public';
import {
  useContentVariations,
  useGlobalClient,
  useNotify,
  useReleases,
  useSharePhoto,
} from 'hooks';
import PublicSocialConnection from './PublicSocialConnection';
import { usePublicPostContext } from '../PublicPostContext';
import { LOCAL_STORAGE_KEY_ACCESS_KEY } from 'app-constants';
import { getClientCommentCharLimit } from 'features/posting/helpers';
import PublicShareErrorMessage from '../partials/PublicShareErrorMessage';
import PublicShareSuccessMessage from '../partials/PublicShareSuccessMessage';
import PublicShareForm from './PublicShareForm';

type IProps = {
  network: Network;
};

const PublicShareTextBox: FC<IProps> = ({ network }) => {
  const notify = useNotify();
  const client = useGlobalClient();
  const { content } = usePublicPostContext().state;
  const contentVariations = useContentVariations();
  const { selectedPhotoIndex } = useSharePhoto();
  const releases = useReleases();

  const hasLinkedInMentions = releases.includes('linkedinCompanyMentions');
  const selectedPhoto = (content?.content_object?.photos || [])[selectedPhotoIndex];
  const limit = getClientCommentCharLimit(network, content, client);
  const publicShareContentMutation = usePublicShareContentMutation({
    onError: handleShareError,
    onSuccess: onShareSuccess,
  });
  const createShareInfoMutation = useCreatePublicShareInfoMutation();

  const shouldShowMentionsEditor = network == Network.Linkedin && hasLinkedInMentions;
  const [hasShared, setHasShared] = useState(false);
  const loading = publicShareContentMutation.isLoading || createShareInfoMutation.isLoading;

  function getVariations() {
    let messages = contentVariations[`${network}Commentary`];
    if (!messages?.length) {
      messages = [
        {
          network,
          commentary: '',
          mentions: null,
          blockList: [],
        },
      ];
    }
    return {
      index: contentVariations[`${network}CommentaryIndex`] || 0,
      messages,
    };
  }

  function handleConnectAndShareToAccount(
    socialAccount: SocialAccount,
    connect: (callback: (socialAccount: SocialAccount) => void) => void,
    shareCopy: ContentCommentary
  ) {
    if (socialAccount) {
      handleShareToAccount(socialAccount, shareCopy);
      return;
    }
    connect(connectedSocialAccount => handleShareToAccount(connectedSocialAccount, shareCopy));
  }

  function handleShareToAccount(socialAccount: SocialAccount, shareCopy: ContentCommentary) {
    return publicShareContentMutation.mutate({
      content_id: content.content_id,
      access_key: localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_KEY),
      items: [
        {
          network_id: socialAccount.id,
          user_commentary: shareCopy.commentary,
          share_type: ShareType.Now,
          photos: selectedPhoto ? [selectedPhoto] : [],
          ...(shouldShowMentionsEditor ? { mentions: shareCopy.mentions ?? [] } : {}),
        },
      ],
    });
  }

  function handleShareToFacebook(shareCopy: ContentCommentary) {
    createShareInfoMutation.mutate(
      {
        access_key: localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_KEY),
        content_id: content.content_id,
      },
      {
        onSuccess: data => {
          const short_link = data.createPublicShareInfo?.shortened_urls?.facebook?.short_link;
          const { content_object } = content;
          const { url } = content_object;

          const href = short_link || url || window?.location?.href;

          window?.FB?.ui(
            {
              href,
              method: 'share',
              quote: shareCopy.commentary,
            },
            () => {
              const variables = {
                access_key: localStorage.getItem(LOCAL_STORAGE_KEY_ACCESS_KEY),
                content_id: content.content_id,
                items: [
                  {
                    share_type: ShareType.Frontend,
                    destination: ShareContentDestination.Facebook,
                    photos: selectedPhoto ? [selectedPhoto] : [],
                  },
                ],
              };
              publicShareContentMutation.mutate(variables);
            }
          );
        },
        onError: handleShareError,
      }
    );
  }

  function onShareSuccess() {
    setHasShared(true);
    notify.info({ component: <PublicShareSuccessMessage network={network} />, duration: 8000 });
  }

  function handleShareError() {
    notify.error({ component: <PublicShareErrorMessage /> });
  }

  function renderContent(
    socialAccount: SocialAccount,
    onSubmit: (shareCopy: ContentCommentary) => void
  ) {
    return (
      <PublicShareForm
        network={network}
        socialAccount={socialAccount}
        variations={getVariations()}
        shouldShowMentionsEditor={shouldShowMentionsEditor}
        onSubmit={onSubmit}
        loading={loading}
        hasShared={hasShared}
        lockCommentary={content.lock_commentary}
        limit={limit}
      />
    );
  }

  function render() {
    if ([Network.Linkedin, Network.Twitter].includes(network)) {
      return (
        <PublicSocialConnection network={network}>
          {(socialAccount, connect) =>
            renderContent(socialAccount, shareCopy =>
              handleConnectAndShareToAccount(socialAccount, connect, shareCopy)
            )
          }
        </PublicSocialConnection>
      );
    }
    if (network == Network.Facebook) {
      return renderContent(null, handleShareToFacebook);
    }
    throw new Error(`unsupported_network:${network}`);
  }

  return render();
};

export default PublicShareTextBox;
